// src/MessagesContext.js
//
// Holds the unread-message count for the navbar badge and a real-time event
// stream (Server-Sent Events) for new messages. We read the SSE stream with the
// native fetch streaming API rather than the browser EventSource, because
// EventSource can't send the Authorization header (and we don't want the JWT in
// a query string). A 60s poll + reconnect-with-backoff keeps it resilient.
import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import api from "./api";
import { AuthContext } from "./AuthContext";

export const MessagesContext = createContext({
  unread: 0,
  refreshUnread: () => {},
  lastEvent: null,
});

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000/api";
const FALLBACK_POLL_MS = 60000;

export function MessagesProvider({ children }) {
  const { user, token } = useContext(AuthContext);
  const [unread, setUnread] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);

  const refreshUnread = useCallback(() => {
    // No staff endpoints are reachable until a forced password change is done.
    if (!user || user.must_change_password) return;
    api
      .get("/messages/unread-count")
      .then((r) => setUnread(r.data?.unread || 0))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || !token || user.must_change_password) {
      setUnread(0);
      return;
    }

    refreshUnread();

    const ctrl = new AbortController();
    let closed = false;
    let retryMs = 3000;

    async function connect() {
      while (!closed) {
        try {
          const res = await fetch(`${API_BASE}/messages/stream`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
            signal: ctrl.signal,
          });
          // Auth gone: stop streaming. Regular API calls will drive the logout.
          if (res.status === 401 || res.status === 403) return;
          if (!res.ok || !res.body) throw new Error(`stream status ${res.status}`);

          retryMs = 3000; // reset backoff after a good connection
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (!closed) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            let idx;
            while ((idx = buffer.indexOf("\n\n")) !== -1) {
              const rawEvent = buffer.slice(0, idx);
              buffer = buffer.slice(idx + 2);
              const dataLines = rawEvent
                .split("\n")
                .filter((l) => l.startsWith("data:"))
                .map((l) => l.slice(5).trim());
              if (dataLines.length === 0) continue; // comment / keepalive
              try {
                const evt = JSON.parse(dataLines.join("\n"));
                setLastEvent({ ...evt, _ts: Date.now() });
                refreshUnread();
              } catch {
                /* ignore malformed event */
              }
            }
          }
        } catch {
          if (closed || ctrl.signal.aborted) return;
        }
        // Reconnect with exponential backoff (capped).
        await new Promise((r) => setTimeout(r, retryMs));
        retryMs = Math.min(retryMs * 2, 30000);
      }
    }

    connect();

    const poll = setInterval(refreshUnread, FALLBACK_POLL_MS);
    const onFocus = () => refreshUnread();
    window.addEventListener("focus", onFocus);

    return () => {
      closed = true;
      ctrl.abort();
      clearInterval(poll);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, token, refreshUnread]);

  return (
    <MessagesContext.Provider value={{ unread, refreshUnread, lastEvent }}>
      {children}
    </MessagesContext.Provider>
  );
}
